import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Plus } from "lucide-react";
import { api, errMsg, TYPE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FindingCard } from "@/components/FindingCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const RISK = { low: "var(--green)", medium: "var(--amber)", high: "var(--red)" };

const issuesText = (r) =>
  r.findings.map((f, i) => `${i + 1}. [${f.severity.toUpperCase()}] ${f.title}${f.page ? ` (p.${f.page})` : ""}\n   "${f.quote}"\n   Why: ${f.why}\n   Action: ${f.action}`).join("\n\n");

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));

const Counts = ({ findings }) => (
  <div className="grid grid-cols-3 border border-[var(--rule)] bg-[var(--card)]">
    {[["red", "High"], ["amber", "Watch"], ["green", "OK"]].map(([k, l]) => (
      <div key={k} className="p-5 border-r last:border-r-0 border-[var(--rule)]">
        <p className="font-serif-d text-4xl" style={{ color: `var(--${k})` }} data-testid={`count-${k}`}>{findings.filter((f) => f.severity === k).length}</p>
        <p className="eyebrow mt-1">{l}</p>
      </div>
    ))}
  </div>
);

export default function ReviewResult() {
  const { id } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get(`/reviews/${id}`).then((x) => setR(x.data)).catch((e) => toast.error(errMsg(e))); }, [id]);

  if (!r) return <div className="max-w-6xl mx-auto px-6 pt-20 eyebrow" data-testid="review-loading">Loading review...</div>;
  const shown = filter === "all" ? r.findings : r.findings.filter((f) => f.severity === filter);

  return (
    <div className="max-w-6xl mx-auto px-6 pt-14" data-testid="review-result-page">
      <div className="flex flex-wrap items-center gap-3 mb-6 font-mono-d text-xs text-[var(--ink-3)]">
        <span className="px-2 py-1 bg-[var(--paper-2)] text-[var(--ink)]" data-testid="review-type">{TYPE_LABELS[r.doc_type]}</span>
        <span data-testid="review-filename">{r.filename}</span>
        <span>{new Date(r.created_at).toLocaleString()}</span>
      </div>
      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-8 rise">
          <h1 className="font-serif-d text-4xl sm:text-5xl tracking-tight leading-tight mb-5" data-testid="review-headline">{r.headline}</h1>
          <p className="text-lg text-[var(--ink-2)] leading-relaxed" data-testid="review-summary">{r.summary}</p>
          <p className="mt-6 eyebrow">Overall risk: <span style={{ color: RISK[r.overall_risk] }} data-testid="review-overall-risk">{r.overall_risk}</span></p>
        </div>
        <div className="lg:col-span-4 space-y-4">
          <Counts findings={r.findings} />
          {r.key_facts?.length > 0 && (
            <dl className="border border-[var(--rule)] bg-[var(--card)] divide-y divide-[var(--rule)]" data-testid="key-facts">
              {r.key_facts.map((k, i) => (
                <div key={i} className="px-5 py-3 grid grid-cols-5 gap-3 text-sm">
                  <dt className="col-span-2 eyebrow pt-0.5">{k.label}</dt><dd className="col-span-3">{k.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      <Tabs defaultValue="issues">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[var(--rule)] mb-8">
          <TabsList className="bg-transparent p-0 h-auto gap-6">
            <TabsTrigger value="issues" data-testid="tab-issues" className="rounded-none px-0 pb-3 bg-transparent data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--ink)] font-serif-d text-xl">Issues</TabsTrigger>
            <TabsTrigger value="memo" data-testid="tab-memo" className="rounded-none px-0 pb-3 bg-transparent data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--ink)] font-serif-d text-xl">Memo</TabsTrigger>
          </TabsList>
          <div className="flex gap-2 pb-3">
            <button onClick={() => copy(issuesText(r))} className="btn-line text-xs px-3 py-2 inline-flex items-center gap-1.5" data-testid="copy-issues-btn"><Copy className="w-3.5 h-3.5" /> Copy issues</button>
            <button onClick={() => copy(r.memo)} className="btn-line text-xs px-3 py-2 inline-flex items-center gap-1.5" data-testid="copy-memo-btn"><Copy className="w-3.5 h-3.5" /> Copy memo</button>
          </div>
        </div>
        <TabsContent value="issues">
          <div className="flex gap-2 mb-6">
            {["all", "red", "amber", "green"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                className={`font-mono-d text-[11px] uppercase tracking-widest px-3 py-1.5 border ${filter === f ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "border-[var(--rule)] hover:border-[var(--ink)]"}`}>
                {f === "all" ? "All" : f === "red" ? "High" : f === "amber" ? "Watch" : "OK"}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {shown.map((f, i) => <FindingCard key={`${filter}-${i}`} finding={f} index={i} />)}
          </div>
        </TabsContent>
        <TabsContent value="memo">
          <div className="bg-[var(--card)] border border-[var(--rule)] p-8 md:p-12 max-w-3xl" data-testid="review-memo">
            <p className="eyebrow mb-6">Memo / {r.filename}</p>
            <div className="font-serif-d text-lg leading-relaxed whitespace-pre-wrap">{r.memo}</div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-14 flex flex-wrap gap-4 items-center">
        <Link to="/new" className="btn-ink px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="review-another-btn"><Plus className="w-4 h-4" /> Review another document</Link>
        {!user && <p className="text-sm text-[var(--ink-2)]"><Link to="/register" className="underline underline-offset-4" data-testid="save-history-link">Create an account</Link> to keep a history of your reviews.</p>}
      </div>
    </div>
  );
}
